// src/utils/storage.ts

/**
 * Safely set an item in localStorage with quota exceeded error handling
 * @param key - The key to store the value under
 * @param value - The value to store (will be stringified if object)
 * @param maxRetries - Maximum number of retry attempts with cleanup
 * @returns boolean indicating success
 */
export const safeSetItem = (key: string, value: any, maxRetries = 1): boolean => {
  try {
    const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, stringValue);
    return true;
  } catch (error: any) {
    if (error.name === 'QuotaExceededError' || error.code === 22) {
      console.warn(`LocalStorage quota exceeded for key: ${key}`);
      
      if (maxRetries > 0) {
        // Try to free up space by removing old or large items
        try {
          // Clear old notifications if they exist and are not the current key
          if (key !== 'notifications' && localStorage.getItem('notifications')) {
            const notifications = JSON.parse(localStorage.getItem('notifications') || '[]');
            if (notifications.length > 50) {
              localStorage.setItem('notifications', JSON.stringify(notifications.slice(0, 50)));
              console.log('Trimmed notifications to free up space');
            }
          }
          
          // Retry setting the item
          return safeSetItem(key, value, maxRetries - 1);
        } catch (cleanupError) {
          console.error('Failed to cleanup localStorage:', cleanupError);
        }
      }
      
      // Last resort: clear the specific key
      console.error(`Failed to save ${key} to localStorage even after cleanup`);
      localStorage.removeItem(key);
      return false;
    } else {
      console.error(`Error saving ${key} to localStorage:`, error);
      return false;
    }
  }
};

/**
 * Safely get an item from localStorage with error handling
 * @param key - The key to retrieve
 * @param defaultValue - Default value to return if retrieval fails
 * @returns The retrieved value or default value
 */
export const safeGetItem = <T = any>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    
    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(item) as T;
    } catch {
      return item as T;
    }
  } catch (error) {
    console.error(`Error reading ${key} from localStorage:`, error);
    return defaultValue;
  }
};

/**
 * Safely remove an item from localStorage with error handling
 * @param key - The key to remove
 * @returns boolean indicating success
 */
export const safeRemoveItem = (key: string): boolean => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing ${key} from localStorage:`, error);
    return false;
  }
};

/**
 * Get the current localStorage usage information
 * @returns Object with used and available space information
 */
export const getStorageInfo = (): { used: number; total: number; available: number } => {
  let used = 0;
  try {
    for (const key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        used += localStorage[key].length + key.length;
      }
    }
  } catch (error) {
    console.error('Error calculating localStorage usage:', error);
  }
  
  // Most browsers have 5-10MB limit, we'll use 5MB as conservative estimate
  const total = 5 * 1024 * 1024; // 5MB in bytes
  const available = Math.max(0, total - used);
  
  return { used, total, available };
};

export const getRememberMeStatus = (): boolean => {
    const rememberMe = localStorage.getItem("rememberMe"); // You can store this value as a string in localStorage
    return rememberMe === "true";
};
  